'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function Navbar() {
  const router = useRouter();
  const { user, loading, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountType, setAccountType] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!user) {
      setAccountType(null);
      return;
    }
    const supabase = createClient();
    supabase?.from('user_profiles')?.select('account_type')?.eq('id', user?.id)?.single()?.then(({ data }) => {
        if (data?.account_type) setAccountType(data?.account_type);
      });
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    router?.push('/');
    setMobileOpen(false);
  };

  const navLinks = [
    { label: 'For Sellers', href: '/#for-sellers' },
    { label: 'For Brands', href: '/#for-brands' },
    { label: 'Browse Sellers', href: '/sellers' },
    { label: 'Job Board', href: '/jobs' },
    { label: 'Pricing', href: '/#pricing' },
  ];

  const dashboardHref = accountType === 'seller' ? '/dashboard/seller' : accountType === 'business' ? '/dashboard/business' : '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-xl bg-dark flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2L18 7V13L10 18L2 13V7L10 2Z" fill="#C9FF85" />
                <path d="M10 6L14 8.5V13.5L10 16L6 13.5V8.5L10 6Z" fill="#120A0B" />
              </svg>
            </div>
            <span className="text-dark font-bold text-xl tracking-tight">Talent</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks?.map((item) => (
              <Link
                key={item?.label}
                href={item?.href}
                className="text-dark font-medium text-[15px] hover:opacity-60 transition-opacity duration-200"
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {loading ? (
              <div className="w-20 h-9 bg-neutral-02 rounded-pill animate-pulse" />
            ) : user ? (
              <>
                <Link
                  href={dashboardHref}
                  className="font-bold text-[15px] text-dark px-6 py-3 rounded-pill hover:opacity-60 transition-opacity duration-200"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleSignOut}
                  className="font-bold text-[15px] text-white bg-dark px-6 py-3 rounded-pill hover:bg-opacity-80 transition-all duration-200"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="font-bold text-[15px] text-dark px-6 py-3 rounded-pill hover:opacity-60 transition-opacity duration-200"
                >
                  Log in
                </Link>
                <Link
                  href="/signup"
                  className="font-bold text-[15px] text-white bg-dark px-6 py-3 rounded-pill hover:bg-opacity-80 transition-all duration-200"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-dark transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-dark transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-dark transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-neutral-02 pt-4">
            <nav className="flex flex-col gap-4">
              {navLinks?.map((item) => (
                <Link
                  key={item?.label}
                  href={item?.href}
                  className="text-dark font-medium text-[15px]"
                  onClick={() => setMobileOpen(false)}
                >
                  {item?.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-2">
                {user ? (
                  <>
                    <Link
                      href={dashboardHref}
                      className="font-bold text-[15px] text-dark text-center py-3 border border-dark rounded-pill"
                      onClick={() => setMobileOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="font-bold text-[15px] text-white bg-dark text-center py-3 rounded-pill"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      className="font-bold text-[15px] text-dark text-center py-3 border border-dark rounded-pill"
                      onClick={() => setMobileOpen(false)}
                    >
                      Log in
                    </Link>
                    <Link
                      href="/signup"
                      className="font-bold text-[15px] text-white bg-dark text-center py-3 rounded-pill"
                      onClick={() => setMobileOpen(false)}
                    >
                      Get Started Free
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
